import { IPoint2d } from '@/editor/math/IPoint2d';
import { modelManager } from '@/editor/manager/entityManager';
import { globalStores, TGlobalStores } from '@/editor/stores';
import { EEntityType } from '@/editor/types';
import { OperateAbstract } from './OperateAbstract';
import BaseEntity from '@/editor/entities/BaseEntity';
import { Vector2 } from 'three';
import { EAreaDimensionType, IDimensionPoint } from '@/editor/data/dimension/dimension';
import { MeasureAbsorbManager } from '@/editor/manager/absorb/measure/measureAbsorbManager';
import { cloneDeep } from 'lodash-es';
import { pixelRatio2d, thresholdValue } from '@/editor/math/const';
import { Point2dUtils } from '@/editor/math/Point2dUtils';
import { LineSeg2d } from '@/editor/math/LineSeg2d';
import { AreaDimensionModel } from '@/editor/entities/2d/areaDimensionModel';
import { isSimplePolygon } from '@/editor/math/utils';
import { VirtualAreaDimensionTextInfo } from '@/editor/entities/2d/textInfo/virtualAreaDimensionTextInfo';

const LIMIT_SIZE = 70.7; // 70.7mm 对应最小四舍五入后0.01平方米
const LIMIT_VEC_SIZE = LIMIT_SIZE / pixelRatio2d;
const AREA_DIMENSION_VIRTUAL_ID = 'areaDimensionVirtual';
export class OperateAreaTransform extends OperateAbstract<EEntityType.AreaDimension> {
  private _checkedEntity: BaseEntity<EEntityType.AreaDimension> | null | undefined = null;
  private _transformManager = modelManager.transformManager;
  private _areaDimensionVirtualModel = new AreaDimensionModel({ id: AREA_DIMENSION_VIRTUAL_ID, points: [] });
  private _virtualTextInfo = new VirtualAreaDimensionTextInfo();
  private _absorbManger: MeasureAbsorbManager;
  private _gs: TGlobalStores = globalStores;
  private _dimensionType: EAreaDimensionType;
  private _controlPointId: string[] = [];
  private _dimensionData: { id: string; polygon: IDimensionPoint[] } = { id: '', polygon: [] };
  private _newDimensionData: { id: string; polygon: IDimensionPoint[] } = { id: '', polygon: [] };
  private _startPoint: IPoint2d | null = null;
  private _startMoveRafId: number | null = null;

  static _v1 = new Vector2(0, 0);
  static _v2 = new Vector2(0, 0);

  constructor() {
    super();
    this._absorbManger = new MeasureAbsorbManager(this._gs);
    this._absorbManger.updateBaseData({
      areaDimension: { enabled: true, excludeIds: [this._gs.editorStateStore.selected.entityId] }
    });
    const { dimensionType } = this._transformManager.getAreaTransformData();
    this._dimensionType = dimensionType;
  }

  private _updateVirtualModel(points: IPoint2d[]) {
    this._areaDimensionVirtualModel.updateModel(
      {
        id: AREA_DIMENSION_VIRTUAL_ID,
        points
      },
      { closed: true, fill: true }
    );
  }

  private get _currentOperateSelected() {
    const globalSelected = globalStores.editorStateStore.selected;
    return globalSelected.entityId ? globalSelected : null;
  }

  set checkedEntity(entity: BaseEntity<EEntityType.AreaDimension> | null | undefined) {
    if (!entity || !this._currentOperateSelected) return;
    this._checkedEntity = entity;

    const selectedEntity = modelManager.getEntity(
      this._currentOperateSelected.entityId,
      this._currentOperateSelected.type
    );
    if (selectedEntity) {
      selectedEntity.selected = true;
    }
  }

  get checkedEntity() {
    return this._checkedEntity;
  }

  get controlPointIds() {
    return this._controlPointId;
  }

  set controlPointIds(ids: string[]) {
    this._controlPointId = [...ids];
  }

  /**
   * 变换期让原件让位给虚拟件
   *
   * 直接操作 entity，可见性只由本类持有、不进 store：变换是纯交互态，
   * 一旦写进显隐 store，中断没恢复就会污染显隐面板（勾选框半选、「显示全部」常亮）
   */
  private _setRealEntityVisible(visible: boolean) {
    const { id } = this._dimensionData;
    if (!id) return;

    modelManager.dimensionManager.getAreaDimension(id)?.setVisible(visible);
    modelManager.textInfoManager.setAreaDimensionTextInfoVisible(id, visible);
  }

  private _setAbsorbNormalData() {
    this._absorbManger.clearNormalData();
    if (this._controlPointId.length === 2 && this._dimensionType === EAreaDimensionType.POLYGON) {
      const startPoint = this._dimensionData.polygon.find((item) => item.id === this._controlPointId[0]);
      const endPoint = this._dimensionData.polygon.find((item) => item.id === this._controlPointId[1]);
      if (startPoint && endPoint) {
        this._absorbManger.setNormalData(startPoint, endPoint);
      }
    }
  }

  override startMove(point: IPoint2d) {
    this._startPoint = point;
    this._startMoveRafId = requestAnimationFrame(() => {
      this._startMoveRafId = null;
      const dimensionData = this._gs.dimensionStore.getAreaDim(this._gs.editorStateStore.selected.entityId);
      if (dimensionData) {
        this._areaDimensionVirtualModel.clear();
        this._dimensionData = {
          id: dimensionData.id,
          polygon: cloneDeep(dimensionData.points)
        };
        this._setAbsorbNormalData();
        this._setRealEntityVisible(false);
        this._virtualTextInfo.update(dimensionData.points, { selected: true });
        this._updateVirtualModel(dimensionData.points);
      }
    });
  }

  move(point: IPoint2d) {
    this.moving = true;
    point = this._absorbManger.calculateAbsorbPointThenRender(point) ?? point;
    let newPolygon: IDimensionPoint[] = [];

    if (this._controlPointId.length < 2) {
      // 拖动的是点
      newPolygon = this._moveEndpointHandler(point);
    } else if (this._controlPointId.length === 2) {
      // 拖动的是线
      newPolygon = this._moveEdgeHandler(point);
    }

    this._newDimensionData.polygon = cloneDeep(newPolygon);
    if (newPolygon.length) {
      const rafId = requestAnimationFrame(() => {
        this._updateVirtualModel(newPolygon);
        modelManager.transformManager.updateAreaTransform(newPolygon);
        this._virtualTextInfo.update(newPolygon, { selected: true });
        cancelAnimationFrame(rafId);
      });
    }

    return point;
  }

  // ========== 点拖动 ========== //
  private _moveEndpointHandler(currentPoint: IPoint2d): IDimensionPoint[] {
    const polygon = this._dimensionData.polygon;

    const handleIdx = polygon.findIndex((item) => item.id === this._controlPointId[0]);
    if (handleIdx === -1) return [];

    const otherPoints = cloneDeep(polygon);
    otherPoints.splice(handleIdx, 1);
    if (hasSamePoint(currentPoint, ...otherPoints)) return [];

    return this._dimensionType === EAreaDimensionType.RECT
      ? this._rectEndpointHandler(currentPoint, handleIdx)
      : this._polygonEndpointHandler(currentPoint, handleIdx);
  }

  // 矩形端点拖动
  private _rectEndpointHandler(currentPoint: IPoint2d, handleIdx: number): IDimensionPoint[] {
    const polygon = this._dimensionData.polygon;

    const pivotIdx = (handleIdx + 2) % 4; // 对角点作为缩放枢轴
    const rIdx = (pivotIdx + 1) % 4; // pivot 右侧邻点
    const lIdx = (pivotIdx + 3) % 4; // pivot 左侧邻点

    // 统一转换为局部无像素比坐标
    const verticesVec = polygon.map(toVec);
    const pivot = verticesVec[pivotIdx];

    // 局部坐标轴（枢轴 -> 右/左邻点）
    const axisU = verticesVec[rIdx].clone().sub(pivot);
    const axisV = verticesVec[lIdx].clone().sub(pivot);

    const lenU = axisU.length();
    const lenV = axisV.length();
    if (lenU < thresholdValue || lenV < thresholdValue) return [];

    const uDir = axisU.divideScalar(lenU); // 局部 X 轴
    const vDir = axisV.divideScalar(lenV); // 局部 Y 轴

    // handle 在局部坐标系中固定为对角位置 (lenU, lenV)
    const mouseOffset = toVec(currentPoint).sub(pivot);
    const mouseU = mouseOffset.dot(uDir);
    const mouseV = mouseOffset.dot(vDir);

    const scaleU = mouseU / lenU;
    const scaleV = mouseV / lenV;

    if (Math.abs(lenU * scaleU) < LIMIT_VEC_SIZE && Math.abs(lenV * scaleV) < LIMIT_VEC_SIZE) {
      return [];
    }

    // 缩放后的基向量
    const scaledU = uDir.clone().multiplyScalar(scaleU);
    const scaledV = vDir.clone().multiplyScalar(scaleV);

    return verticesVec.map((point, index) => {
      const relative = point.clone().sub(pivot);
      const u = relative.dot(uDir);
      const v = relative.dot(vDir);

      const scaled = pivot.clone().add(scaledU.clone().multiplyScalar(u)).add(scaledV.clone().multiplyScalar(v));

      return {
        ...polygon[index],
        x: scaled.x * pixelRatio2d,
        y: scaled.y * pixelRatio2d
      };
    });
  }

  // 多边形端点拖动
  private _polygonEndpointHandler(currentPoint: IPoint2d, handleIdx: number): IDimensionPoint[] {
    const polygon = this._dimensionData.polygon;

    const nextPolygon = cloneDeep(polygon);
    nextPolygon[handleIdx] = { ...nextPolygon[handleIdx], ...currentPoint };

    for (let i = 0; i < nextPolygon.length; i++) {
      const next = (i + 1) % nextPolygon.length;
      // 忽略包含当前顶点的边
      if (i === handleIdx || next === handleIdx) continue;
      if (new LineSeg2d(nextPolygon[i], nextPolygon[next]).pointOnSegment(currentPoint)) {
        return [];
      }
    }

    for (let i = 0; i < nextPolygon.length; i++) {
      const a1 = nextPolygon[i];
      const a2 = nextPolygon[(i + 1) % nextPolygon.length];

      for (let j = i + 1; j < nextPolygon.length; j++) {
        const b1 = nextPolygon[j];
        const b2 = nextPolygon[(j + 1) % nextPolygon.length];

        // 邻接边（共享端点）不需要判断
        if (hasSamePoint(a1, b1, b2) || hasSamePoint(a2, b1, b2)) {
          continue;
        }

        if (new LineSeg2d(a1, a2).intersection(new LineSeg2d(b1, b2))) {
          return [];
        }
      }
    }

    return nextPolygon;
  }

  // ========== 线拖动 ========== //
  private _moveEdgeHandler(currentPoint: IPoint2d): IDimensionPoint[] {
    const polygon = this._dimensionData.polygon;
    const [startId, endId] = this._controlPointId;
    const startIdx = polygon.findIndex((item) => item.id === startId);
    const endIdx = polygon.findIndex((item) => item.id === endId);
    if (startIdx < 0 || endIdx < 0) return [];

    return this._dimensionType === EAreaDimensionType.RECT
      ? this._moveRectEdgeHandler(currentPoint, startIdx, endIdx)
      : this._movePolygonEdgeHandler(currentPoint, startIdx, endIdx);
  }

  private _moveRectEdgeHandler(currentPoint: IPoint2d, startIdx: number, endIdx: number): IDimensionPoint[] {
    const polygon = this._dimensionData.polygon;

    const startPrevIdx = (endIdx + 2) % 4;

    // 统一转换为局部无像素比坐标
    const verticesVec = polygon.map(toVec);

    const startVec = verticesVec[startIdx];
    const endVec = verticesVec[endIdx];

    const edgeVec = new Vector2().subVectors(endVec, startVec);
    const edgeLen = edgeVec.length();
    if (edgeLen < thresholdValue) {
      return [];
    }

    // 外侧法线
    const outwardNormal = new Vector2(edgeVec.y, -edgeVec.x).normalize();
    const mouseVec = new Vector2().subVectors(toVec(currentPoint), startVec);
    const offset = mouseVec.dot(outwardNormal); // 鼠标沿法线方向的位移量

    const offsetVec = new Vector2(outwardNormal.x * offset, outwardNormal.y * offset);
    startVec.add(offsetVec);
    endVec.add(offsetVec);

    if (new Vector2().subVectors(startVec, verticesVec[startPrevIdx]).length() < LIMIT_VEC_SIZE) {
      return [];
    }

    return verticesVec.map((point, index) => {
      if (index === startIdx) return { ...polygon[index], x: startVec.x * pixelRatio2d, y: startVec.y * pixelRatio2d };
      else if (index === endIdx) return { ...polygon[index], x: endVec.x * pixelRatio2d, y: endVec.y * pixelRatio2d };

      return {
        ...polygon[index],
        x: point.x * pixelRatio2d,
        y: point.y * pixelRatio2d
      };
    });
  }

  private _movePolygonEdgeHandler(currentPoint: IPoint2d, startIdx: number, endIdx: number): IDimensionPoint[] {
    const polygon = this._dimensionData.polygon;
    const vertexCount = polygon.length;

    if (vertexCount < 3) return [];
    if (!this._startPoint) return [];

    // 用当前鼠标位置 - 起始鼠标位置，得到真实的自由移动偏移量
    const mouseVec = toVec(currentPoint);
    const deltaVec = new Vector2().subVectors(mouseVec, toVec(this._startPoint));

    if (deltaVec.lengthSq() < thresholdValue) {
      return [];
    }

    const verticesVec = polygon.map(toVec);

    const startVec = verticesVec[startIdx];
    const endVec = verticesVec[endIdx];

    // 验证边长
    const edgeVec = new Vector2().subVectors(endVec, startVec);
    const edgeLen = edgeVec.length();
    if (edgeLen < thresholdValue) return [];

    // 只移动操作边的两个端点
    startVec.add(deltaVec);
    endVec.add(deltaVec);

    const prevIdx = (startIdx - 1 + vertexCount) % vertexCount;
    const nextIdx = (endIdx + 1) % vertexCount;

    // 1mm 限制
    if (verticesVec[prevIdx].distanceTo(startVec) < 1 / pixelRatio2d) return [];
    if (verticesVec[nextIdx].distanceTo(endVec) < 1 / pixelRatio2d) return [];

    const newPolygonPoints = verticesVec.map((point, index) => ({
      ...polygon[index],
      x: point.x * pixelRatio2d,
      y: point.y * pixelRatio2d
    }));

    if (!isSimplePolygon(newPolygonPoints, Math.pow(LIMIT_SIZE, 2))) return [];

    return newPolygonPoints;
  }

  completeMove(_point: IPoint2d) {
    this._gs.dimensionOperation.modifyAreaDimension(
      this._dimensionData.id,
      this._newDimensionData.polygon.length ? this._newDimensionData.polygon : this._dimensionData.polygon
    );

    this.clear();
  }

  clear() {
    // 拖拽在 raf 回调执行前就被中断：取消它，否则回调会在 clear 之后才把标注隐藏掉
    if (this._startMoveRafId !== null) {
      cancelAnimationFrame(this._startMoveRafId);
      this._startMoveRafId = null;
    }
    this._areaDimensionVirtualModel.clear();
    this._virtualTextInfo.clear();
    this._absorbManger.clearNormalData();
    this.checkedEntity = null;
    this.moving = false;
    this._startPoint = null;
    this._absorbManger.clearOrthogonalData();
    this._absorbManger.clearModel();

    // 原件的恢复必须放在 clear：正常完成（completeMove）和中断（dispose）都会走到这里，
    // 只在 completeMove 里恢复的话，中断的拖拽会让标注一直隐身
    this._setRealEntityVisible(true);
    this._dimensionData = { id: '', polygon: [] };
  }

  dispose() {
    this.clear();
  }
}

function hasSamePoint(currentPoint: IPoint2d, ...checkPoints: IPoint2d[]): boolean {
  let result = false;

  for (const point of checkPoints) {
    if (Point2dUtils.equals(point, currentPoint)) {
      result = true;
      break;
    }
  }

  return result;
}

function toVec(pt: IPoint2d) {
  return new Vector2(pt.x / pixelRatio2d, pt.y / pixelRatio2d);
}
